/**
 * Image utility functions for patient photos and UI elements
 */

// Placeholder images for different species
export const SPECIES_PLACEHOLDERS = {
  dog: '/api/images/placeholders/dog-placeholder.jpg',
  cat: '/api/images/placeholders/cat-placeholder.jpg',
  bird: '/api/images/placeholders/bird-placeholder.jpg',
  rabbit: '/api/images/placeholders/rabbit-placeholder.jpg',
  hamster: '/api/images/placeholders/hamster-placeholder.jpg',
  other: '/api/images/placeholders/default-patient.jpg',
} as const;

export type SpeciesType = keyof typeof SPECIES_PLACEHOLDERS;

/**
 * Get placeholder image for a specific species
 */
export const getSpeciesPlaceholder = (species: string): string => {
  const normalizedSpecies = species.toLowerCase();

  // Check if the species exists in our placeholders
  if (normalizedSpecies in SPECIES_PLACEHOLDERS) {
    return SPECIES_PLACEHOLDERS[normalizedSpecies as SpeciesType];
  }

  // Return default placeholder for unknown species
  return SPECIES_PLACEHOLDERS.other;
};

/**
 * Get patient image URL with fallback logic
 */
export const getPatientImageUrl = (
  photoUrl?: string,
  species?: string
): string => {
  // If patient has a photo, use it
  if (photoUrl && photoUrl.trim() !== '') {
    return photoUrl;
  }

  // Otherwise use species placeholder
  if (species) {
    return getSpeciesPlaceholder(species);
  }

  // Default placeholder
  return SPECIES_PLACEHOLDERS.other;
};

/**
 * Generate avatar initials from name
 */
export const generateAvatarInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);

  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a color based on name for avatar background
 */
export const generateAvatarColor = (name: string): string => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#FD79A8', '#A29BFE', '#6C5CE7', '#00B894', '#FDCB6E',
    '#E17055', '#0984E3', '#74B9FF', '#A29BFE', '#81ECEC',
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

/**
 * Validate image file type
 */
export const isValidImageType = (file: File): boolean => {
  const validTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/gif',
  ];

  return validTypes.includes(file.type);
};

/**
 * Validate image file size (max 5MB)
 */
export const isValidImageSize = (file: File): boolean => {
  const maxSize = 5 * 1024 * 1024; // 5MB
  return file.size <= maxSize;
};

/**
 * Get image file validation error message
 */
export const getImageValidationError = (file: File): string | null => {
  if (!isValidImageType(file)) {
    return 'Invalid file type. Please upload JPG, PNG, WebP, or GIF images only.';
  }

  if (!isValidImageSize(file)) {
    return 'File size too large. Please upload images smaller than 5MB.';
  }

  return null;
};

/**
 * Create image preview from file
 */
export const createImagePreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      if (e.target?.result) {
        resolve(e.target.result as string);
      } else {
        reject(new Error('Failed to create image preview'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};